import { JSONRPCServer } from "json-rpc-2.0";
import {
  CreateAnchoredShapeOptions,
  CreateMultipointShapeOptions,
  CreateShapeOptions,
  EntityId,
  EmojiOptions,
  IconOptions,
  PositionPercents,
  ShapePoint,
  SupportedLineTools,
} from "../../public/charting_library/charting_library";
import { BehaviorSubject, Subscription } from "rxjs";
import { _globalService } from "./_global.service";
import { _chartReadyService } from "./_chartReady.service";
import config from "../config.json";

type RpcInitParams = {
  version?: number;
};

type RpcSetParams = {
  symbol?: string;
  socket_url?: string;
  api_url?: string;
  user_token?: string;
  account_token?: string;
  account?: string;
  server?: string;
  theme?: "light" | "dark";
  locale?: string;
  external_interval_control?: boolean;
  default_interval?: string;
  external_indicator_control?: boolean;
};

export type DrawingCreateParams =
  | {
      kind: "single";
      point: ShapePoint;
      options: CreateShapeOptions<Record<string, unknown>>;
    }
  | {
      kind: "multi";
      points: ShapePoint[];
      options: CreateMultipointShapeOptions<Record<string, unknown>>;
    }
  | {
      kind: "anchored";
      position: PositionPercents;
      options: CreateAnchoredShapeOptions<Record<string, unknown>>;
    };

export type DrawingSelectParams = {
  tool: SupportedLineTools;
  options?: IconOptions | EmojiOptions;
};

const DRAWING_TOOLS: SupportedLineTools[] = [
  "text",
  "anchored_text",
  "note",
  "anchored_note",
  "signpost",
  "double_curve",
  "arc",
  "icon",
  "emoji",
  "sticker",
  "arrow_up",
  "arrow_down",
  "arrow_left",
  "arrow_right",
  "price_label",
  "price_note",
  "arrow_marker",
  "flag",
  "vertical_line",
  "horizontal_line",
  "cross_line",
  "horizontal_ray",
  "trend_line",
  "info_line",
  "trend_angle",
  "arrow",
  "ray",
  "extended",
  "parallel_channel",
  "disjoint_angle",
  "flat_bottom",
  "anchored_vwap",
  "pitchfork",
  "schiff_pitchfork_modified",
  "schiff_pitchfork",
  "balloon",
  "comment",
  "inside_pitchfork",
  "pitchfan",
  "gannbox",
  "gannbox_square",
  "gannbox_fixed",
  "gannbox_fan",
  "fib_retracement",
  "fib_trend_ext",
  "fib_speed_resist_fan",
  "fib_timezone",
  "fib_trend_time",
  "fib_circles",
  "fib_spiral",
  "fib_speed_resist_arcs",
  "fib_channel",
  "xabcd_pattern",
  "cypher_pattern",
  "abcd_pattern",
  "callout",
  "triangle_pattern",
  "3divers_pattern",
  "head_and_shoulders",
  "fib_wedge",
  "elliott_impulse_wave",
  "elliott_triangle_wave",
  "elliott_triple_combo",
  "elliott_correction",
  "elliott_double_combo",
  "cyclic_lines",
  "time_cycles",
  "sine_line",
  "long_position",
  "short_position",
  "forecast",
  "date_range",
  "price_range",
  "date_and_price_range",
  "bars_pattern",
  "ghost_feed",
  "projection",
  "rectangle",
  "rotated_rectangle",
  "circle",
  "ellipse",
  "triangle",
  "polyline",
  "path",
  "curve",
  "cursor",
  "dot",
  "arrow_cursor",
  "eraser",
  "measure",
  "zoom",
  "brush",
  "highlighter",
  "regression_trend",
  "fixed_range_volume_profile",
];

class RpcService {
  private readonly server = new JSONRPCServer();
  private rpcEnabled = false;
  private readonly boundWindowHandler = this.handleWindowMessage.bind(this);
  private readonly boundDocumentHandler = this.handleDocumentMessage.bind(this);
  private readonly subs: Subscription[] = [];
  private initialized = false;
  private readonly externalIntervalControl = new BehaviorSubject<boolean | null>(
    null
  );
  private readonly externalIndicatorControl = new BehaviorSubject<
    boolean | null
  >(null);
  private readonly defaultInterval = new BehaviorSubject<string | null>(null);
  private intervalHandler: ((interval: string) => void) | null = null;
  private chartTypeHandler: ((chartType: number) => void) | null = null;
  private toggleChartTypeHandler: (() => void) | null = null;
  private indicatorHandlers: {
    getAvailable: () => string[];
    listActive: () => { id: string | number; name: string }[];
    add: (name: string) => Promise<string | number> | string | number;
    remove: (id: string | number) => boolean;
  } | null = null;
  private drawingHandlers: {
    listActive: () => { id: EntityId; name: string }[];
    add: (params: DrawingCreateParams) => EntityId | null;
    remove: (id: EntityId) => boolean;
    selectTool: (params: DrawingSelectParams) => void;
  } | null = null;
  private pendingIntervalChange: {
    interval: string;
    resolve: (value: boolean) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  } | null = null;
  private intervalChangeTimeoutMs = 3000;
  private pendingChartTypeChange: {
    chartType: number;
    resolve: (value: boolean) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  } | null = null;
  private chartTypeChangeTimeoutMs = 3000;
  private intervalControlTimeoutMs = 3000;
  private indicatorControlTimeoutMs = 3000;

  constructor() {
    this.server.addMethod("rpc.init", (params?: RpcInitParams) => {
      if (params?.version !== 2) {
        throw new Error("Unsupported RPC version");
      }
      this.rpcEnabled = true;
      this.notify("app.ready", {
        version: config.version,
        message: `FXGT ChartView Initialized @version: ${config.version}`,
      });
      return { version: 2 };
    });

    this.server.addMethod("getState", () => {
      this.ensureEnabled();
      return this.getState();
    });

    this.server.addMethod("setParams", async (params: RpcSetParams) => {
      this.ensureEnabled();
      this.applyParams(params);
      if (typeof params.external_interval_control === "boolean") {
        await this.requestIntervalControl(params.external_interval_control);
      }
      if (typeof params.external_indicator_control === "boolean") {
        await this.requestIndicatorControl(params.external_indicator_control);
      }
      return true;
    });

    this.server.addMethod("setApiUrl", (params: { api_url: string }) => {
      this.ensureEnabled();
      _globalService.set_api_url(params.api_url);
      return true;
    });

    this.server.addMethod("setSocketUrl", (params: { socket_url: string }) => {
      this.ensureEnabled();
      _globalService.set_socket_url(params.socket_url);
      return true;
    });

    this.server.addMethod("setUserToken", (params: { user_token: string }) => {
      this.ensureEnabled();
      _globalService.set_user_token(params.user_token);
      return true;
    });

    this.server.addMethod(
      "setAccountToken",
      (params: { account_token: string }) => {
        this.ensureEnabled();
        _globalService.set_account_token(params.account_token);
        return true;
      }
    );

    this.server.addMethod("setAccount", (params: { account: string }) => {
      this.ensureEnabled();
      _globalService.set_account(params.account);
      return true;
    });

    this.server.addMethod("setServer", (params: { server: string }) => {
      this.ensureEnabled();
      _globalService.set_server(params.server);
      return true;
    });

    this.server.addMethod(
      "setIntervalControl",
      (params: { external: boolean }) => {
        this.ensureEnabled();
        return this.requestIntervalControl(Boolean(params.external));
      }
    );

    this.server.addMethod(
      "setIndicatorControl",
      (params: { external: boolean }) => {
        this.ensureEnabled();
        return this.requestIndicatorControl(Boolean(params.external));
      }
    );

    this.server.addMethod("setDefaultInterval", (params: { interval: string }) => {
      this.ensureEnabled();
      this.defaultInterval.next(params.interval);
      return true;
    });

    this.server.addMethod("getIndicators", () => {
      this.ensureEnabled();
      if (!this.indicatorHandlers) {
        throw new Error("Indicator handlers not available");
      }
      return this.indicatorHandlers.getAvailable();
    });

    this.server.addMethod("listActiveIndicators", () => {
      this.ensureEnabled();
      if (!this.indicatorHandlers) {
        throw new Error("Indicator handlers not available");
      }
      return this.indicatorHandlers.listActive();
    });

    this.server.addMethod("addIndicator", async (params: { name: string }) => {
      this.ensureEnabled();
      if (!this.indicatorHandlers) {
        throw new Error("Indicator handlers not available");
      }
      const id = await this.indicatorHandlers.add(params.name);
      return { id };
    });

    this.server.addMethod("removeIndicator", (params: { id: string | number }) => {
      this.ensureEnabled();
      if (!this.indicatorHandlers) {
        throw new Error("Indicator handlers not available");
      }
      return this.indicatorHandlers.remove(params.id);
    });

    this.server.addMethod("getDrawingTools", () => {
      this.ensureEnabled();
      return DRAWING_TOOLS;
    });

    this.server.addMethod("listActiveDrawings", () => {
      this.ensureEnabled();
      if (!this.drawingHandlers) {
        throw new Error("Drawing handlers not available");
      }
      return this.drawingHandlers.listActive();
    });

    this.server.addMethod("addDrawing", (params: DrawingCreateParams) => {
      this.ensureEnabled();
      if (!this.drawingHandlers) {
        throw new Error("Drawing handlers not available");
      }
      const id = this.drawingHandlers.add(params);
      if (!id) {
        throw new Error("Failed to create drawing");
      }
      return { id };
    });

    this.server.addMethod("removeDrawing", (params: { id: EntityId }) => {
      this.ensureEnabled();
      if (!this.drawingHandlers) {
        throw new Error("Drawing handlers not available");
      }
      return this.drawingHandlers.remove(params.id);
    });

    this.server.addMethod("selectDrawingTool", (params: DrawingSelectParams) => {
      this.ensureEnabled();
      if (!this.drawingHandlers) {
        throw new Error("Drawing handlers not available");
      }
      this.drawingHandlers.selectTool(params);
      return true;
    });

    this.server.addMethod("setInterval", (params: { interval: string }) => {
      this.ensureEnabled();
      if (!this.intervalHandler) {
        throw new Error("Interval handler not available");
      }
      return this.requestIntervalChange(params.interval);
    });

    this.server.addMethod("setChartType", (params: { chart_type: number }) => {
      this.ensureEnabled();
      if (!this.chartTypeHandler) {
        throw new Error("Chart type handler not available");
      }
      return this.requestChartTypeChange(params.chart_type);
    });

    this.server.addMethod("toggleChartType", () => {
      this.ensureEnabled();
      if (!this.toggleChartTypeHandler) {
        throw new Error("Chart type handler not available");
      }
      this.toggleChartTypeHandler();
      return true;
    });

    this.server.addMethod("changeSymbol", (params: { symbol: string }) => {
      this.ensureEnabled();
      _globalService.changeSymbol(params.symbol);
      return true;
    });

    this.server.addMethod(
      "changeTheme",
      (params: { theme: "light" | "dark" }) => {
        this.ensureEnabled();
        _globalService.changeTheme(params.theme);
        return true;
      }
    );

    this.server.addMethod("changeLocale", (params: { locale: string }) => {
      this.ensureEnabled();
      _globalService.changeLocale(params.locale);
      return true;
    });
  }

  public init() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    window.addEventListener("message", this.boundWindowHandler);
    document.addEventListener("message", this.boundDocumentHandler);

    this.subs.push(
      _chartReadyService.subToChartReady().subscribe((ready) => {
        this.notify("chart.ready", { ready });
      })
    );

    this.subs.push(
      _globalService.sub_symbolReady().subscribe((ready) => {
        this.notify("symbol.ready", {
          ready,
          symbol: _globalService.get_symbol(),
        });
      })
    );
  }

  public isRpcEnabled() {
    return this.rpcEnabled;
  }

  public subExternalIntervalControl() {
    return this.externalIntervalControl.asObservable();
  }

  public subExternalIndicatorControl() {
    return this.externalIndicatorControl.asObservable();
  }

  public subDefaultInterval() {
    return this.defaultInterval.asObservable();
  }

  public getDefaultInterval() {
    return this.defaultInterval.value;
  }

  public registerIntervalHandler(handler: ((interval: string) => void) | null) {
    this.intervalHandler = handler;
  }

  public registerChartTypeHandler(
    handler: ((chartType: number) => void) | null
  ) {
    this.chartTypeHandler = handler;
  }

  public registerToggleChartTypeHandler(handler: (() => void) | null) {
    this.toggleChartTypeHandler = handler;
  }

  public registerIndicatorHandlers(handlers: {
    getAvailable: () => string[];
    listActive: () => { id: string | number; name: string }[];
    add: (name: string) => Promise<string | number> | string | number;
    remove: (id: string | number) => boolean;
  } | null) {
    this.indicatorHandlers = handlers;
  }

  public registerDrawingHandlers(handlers: {
    listActive: () => { id: EntityId; name: string }[];
    add: (params: DrawingCreateParams) => EntityId | null;
    remove: (id: EntityId) => boolean;
    selectTool: (params: DrawingSelectParams) => void;
  } | null) {
    this.drawingHandlers = handlers;
  }

  public notify(method: string, params?: object) {
    if (!this.rpcEnabled) {
      return;
    }
    if (method === "interval.changed") {
      const interval = (params as { interval?: string })?.interval;
      if (interval) {
        this.resolveIntervalChange(interval);
      }
    }
    if (method === "chartType.changed") {
      const chartType = (params as { chart_type?: number })?.chart_type;
      if (typeof chartType === "number") {
        this.resolveChartTypeChange(chartType);
      }
    }
    const message = {
      jsonrpc: "2.0",
      method,
      params,
    };
    _globalService.postMessage(JSON.stringify(message));
  }

  private ensureEnabled() {
    if (!this.rpcEnabled) {
      throw new Error("RPC not initialized");
    }
  }

  private applyParams(params: RpcSetParams) {
    if (typeof params.api_url === "string") {
      _globalService.set_api_url(params.api_url);
    }
    if (typeof params.socket_url === "string") {
      _globalService.set_socket_url(params.socket_url);
    }
    if (typeof params.user_token === "string") {
      _globalService.set_user_token(params.user_token);
    }
    if (typeof params.account_token === "string") {
      _globalService.set_account_token(params.account_token);
    }
    if (typeof params.account === "string") {
      _globalService.set_account(params.account);
    }
    if (typeof params.server === "string") {
      _globalService.set_server(params.server);
    }
    if (typeof params.theme === "string") {
      _globalService.set_theme(params.theme);
    }
    if (typeof params.locale === "string") {
      _globalService.set_locale(params.locale);
    }
    if (typeof params.symbol === "string") {
      _globalService.changeSymbol(params.symbol);
    }
    if (typeof params.default_interval === "string") {
      this.defaultInterval.next(params.default_interval);
    }
    if (typeof params.external_indicator_control === "boolean") {
      this.externalIndicatorControl.next(params.external_indicator_control);
    }
  }

  private getState() {
    return {
      symbol: _globalService.get_symbol(),
      theme: _globalService.get_theme(),
      locale: _globalService.get_locale(),
      socket_url: _globalService.get_socket_url(),
      api_url: _globalService.get_api_url(),
      user_token: _globalService.get_user_token(),
      account_token: _globalService.get_account_token(),
      account: _globalService.get_account(),
      server: _globalService.get_server(),
      chart_ready: _chartReadyService.getChartIsReady(),
      symbol_ready: _globalService.get_symbolReady(),
      external_interval_control: this.externalIntervalControl.value,
      default_interval: this.defaultInterval.value,
      external_indicator_control: this.externalIndicatorControl.value,
    };
  }

  private requestIntervalChange(interval: string): Promise<boolean> {
    if (this.pendingIntervalChange) {
      this.pendingIntervalChange.reject(
        new Error("Interval change already in progress")
      );
      clearTimeout(this.pendingIntervalChange.timeoutId);
      this.pendingIntervalChange = null;
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        if (this.pendingIntervalChange?.interval === interval) {
          this.pendingIntervalChange = null;
          reject(new Error("Interval change timed out"));
        }
      }, this.intervalChangeTimeoutMs);

      this.pendingIntervalChange = { interval, resolve, reject, timeoutId };

      try {
        this.intervalHandler?.(interval);
      } catch (error) {
        clearTimeout(timeoutId);
        this.pendingIntervalChange = null;
        reject(error as Error);
      }
    });
  }

  private resolveIntervalChange(interval: string) {
    if (!this.pendingIntervalChange) {
      return;
    }
    if (this.pendingIntervalChange.interval !== interval) {
      const error = new Error(
        `Interval change mismatch: expected ${this.pendingIntervalChange.interval}, got ${interval}`
      );
      this.pendingIntervalChange.reject(error);
      clearTimeout(this.pendingIntervalChange.timeoutId);
      this.pendingIntervalChange = null;
      return;
    }

    this.pendingIntervalChange.resolve(true);
    clearTimeout(this.pendingIntervalChange.timeoutId);
    this.pendingIntervalChange = null;
  }

  private async requestIntervalControl(external: boolean): Promise<boolean> {
    if (external && !_chartReadyService.getChartIsReady()) {
      await this.waitForChartReady(this.intervalControlTimeoutMs);
    }
    this.externalIntervalControl.next(external);
    this.notify("intervalControl.changed", { external });
    return true;
  }

  private async requestIndicatorControl(external: boolean): Promise<boolean> {
    if (external && !_chartReadyService.getChartIsReady()) {
      await this.waitForChartReady(this.indicatorControlTimeoutMs);
    }
    this.externalIndicatorControl.next(external);
    this.notify("indicatorControl.changed", { external });
    return true;
  }

  private waitForChartReady(timeoutMs: number): Promise<void> {
    if (_chartReadyService.getChartIsReady()) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        sub.unsubscribe();
        reject(new Error("Chart ready timeout"));
      }, timeoutMs);

      const sub = _chartReadyService.subToChartReady().subscribe((ready) => {
        if (ready) {
          clearTimeout(timeoutId);
          sub.unsubscribe();
          resolve();
        }
      });
    });
  }

  private requestChartTypeChange(chartType: number): Promise<boolean> {
    if (this.pendingChartTypeChange) {
      this.pendingChartTypeChange.reject(
        new Error("Chart type change already in progress")
      );
      clearTimeout(this.pendingChartTypeChange.timeoutId);
      this.pendingChartTypeChange = null;
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        if (this.pendingChartTypeChange?.chartType === chartType) {
          this.pendingChartTypeChange = null;
          reject(new Error("Chart type change timed out"));
        }
      }, this.chartTypeChangeTimeoutMs);

      this.pendingChartTypeChange = {
        chartType,
        resolve,
        reject,
        timeoutId,
      };

      try {
        this.chartTypeHandler?.(chartType);
      } catch (error) {
        clearTimeout(timeoutId);
        this.pendingChartTypeChange = null;
        reject(error as Error);
      }
    });
  }

  private resolveChartTypeChange(chartType: number) {
    if (!this.pendingChartTypeChange) {
      return;
    }
    if (this.pendingChartTypeChange.chartType !== chartType) {
      const error = new Error(
        `Chart type mismatch: expected ${this.pendingChartTypeChange.chartType}, got ${chartType}`
      );
      this.pendingChartTypeChange.reject(error);
      clearTimeout(this.pendingChartTypeChange.timeoutId);
      this.pendingChartTypeChange = null;
      return;
    }

    this.pendingChartTypeChange.resolve(true);
    clearTimeout(this.pendingChartTypeChange.timeoutId);
    this.pendingChartTypeChange = null;
  }

  private handleWindowMessage(event: MessageEvent) {
    this.handleMessageEvent(event);
  }

  private handleDocumentMessage(event: Event) {
    this.handleMessageEvent(event as MessageEvent);
  }

  private async handleMessageEvent(event: MessageEvent) {
    let payload: any = event.data;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch (error) {
        return;
      }
    }

    if (!payload || payload.jsonrpc !== "2.0") {
      return;
    }

    const response = await this.server.receive(payload);
    if (response) {
      _globalService.postMessage(JSON.stringify(response));
    }
  }
}

export const _rpcService = new RpcService();
